import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * NSISymptomTracker
 * DoD/VA Neurobehavioral Symptom Inventory self-check surface.
 * Used in Profile Settings (always visible) AND inlined on the Patient wizard
 * Step 2 (notes) as a collapsible Quick Tool so concussion patients can find
 * it without digging into settings.
 */
const NSISymptomTracker = ({
  defaultOpen = false,
  compact = false,
  variant = 'inline', // 'inline' = visible card in wizard · 'panel' = nested in settings
  testIdPrefix = 'nsi',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const NSI_EDUCATION = `TBI/CONCUSSION — SYMPTOM TRACKER (NSI)
Track these symptoms to share with your provider.
Rate 0-4: None / Mild / Moderate / Severe / Very Severe

THINGS TO WATCH FOR:
- Headaches (getting worse or not going away?)
- Dizziness or balance problems when standing/walking
- Blurred or double vision
- Sensitivity to light or loud noise
- Can't concentrate or remember new things
- Feeling more irritable, anxious, or sad than usual
- Trouble sleeping or sleeping too much
- Nausea
- Numbness or tingling
- Fatigue / getting tired easily
- Poor coordination or clumsy
- Difficulty making decisions

GO TO ER IMMEDIATELY IF:
- Worsening headache that won't stop
- Repeated vomiting
- Seizure
- Weakness or numbness on one side
- Slurred speech or increasing confusion
- Clear fluid from nose or ear

YOUR RESTRICTIONS (ask your provider which stage):
- Limit phone/screen time (take breaks every 30 min)
- No driving until cleared by provider
- No alcohol
- No PT or exercise until cleared
- Rest as much as possible
- If symptoms get WORSE, not better → call your clinic`;

  const NSI_TEMPLATE = `My Symptom Check (NSI):
- Headaches: /4
- Dizziness: /4
- Balance: /4
- Vision problems: /4
- Light sensitivity: /4
- Noise sensitivity: /4
- Concentration: /4
- Memory: /4
- Decisions: /4
- Fatigue: /4
- Sleep: /4
- Anxiety: /4
- Mood: /4
- Irritability: /4

Total: /88

Notes for provider:`;

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(NSI_TEMPLATE);
      toast.success('NSI template copied — fill in your scores and send to provider');
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyEducationPlusTracker = async () => {
    try {
      await navigator.clipboard.writeText(`${NSI_EDUCATION}\n\n---\n\n${NSI_TEMPLATE}`);
      toast.success('Education + tracker copied — full handout ready to paste or send');
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyEducationOnly = async () => {
    try {
      await navigator.clipboard.writeText(NSI_EDUCATION);
      toast.success('Patient education copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const inlineHeaderClasses = variant === 'inline'
    ? 'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors'
    : 'w-full flex items-center gap-2 text-sm font-medium';

  return (
    <div className={variant === 'inline' ? 'space-y-2' : 'space-y-2'} data-testid={`${testIdPrefix}-wrapper`}>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className={inlineHeaderClasses}
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-400" />
          <span className={variant === 'inline' ? 'text-sm font-semibold text-amber-300' : 'text-sm'}>
            Symptom Tracker (NSI)
          </span>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">
            TBI/Concussion
          </span>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-amber-400" /> : <ChevronRight className="w-4 h-4 text-amber-400" />}
      </button>

      {!isOpen && variant === 'inline' && (
        <p className="text-[11px] text-muted-foreground px-3">
          Had a head injury or concussion? Tap to check what symptoms to watch for & copy a form to send your provider.
        </p>
      )}

      {isOpen && (
        <>
          <p className="text-xs text-muted-foreground">
            Track these symptoms to share with your provider. Rate 0-4: None / Mild / Moderate / Severe / Very Severe
          </p>
          <div className={`p-3 bg-secondary rounded-lg space-y-2 ${compact ? 'max-h-[240px]' : 'max-h-[300px]'} overflow-y-auto text-xs`}>
            <p className="font-bold text-foreground">Things to watch for:</p>
            <div className="space-y-1.5 text-muted-foreground">
              <p>- Headaches (getting worse or not going away?)</p>
              <p>- Dizziness or balance problems when standing/walking</p>
              <p>- Blurred or double vision</p>
              <p>- Sensitivity to light or loud noise</p>
              <p>- Can't concentrate or remember new things</p>
              <p>- Feeling more irritable, anxious, or sad than usual</p>
              <p>- Trouble sleeping or sleeping too much</p>
              <p>- Nausea</p>
              <p>- Numbness or tingling</p>
              <p>- Fatigue / getting tired easily</p>
              <p>- Poor coordination or clumsy</p>
              <p>- Difficulty making decisions</p>
            </div>
            <div className="border-t border-border/50 pt-2 mt-2">
              <p className="font-bold text-red-400">Go to ER IMMEDIATELY if:</p>
              <p className="text-red-400/80">- Worsening headache that won't stop</p>
              <p className="text-red-400/80">- Repeated vomiting</p>
              <p className="text-red-400/80">- Seizure</p>
              <p className="text-red-400/80">- Weakness or numbness on one side</p>
              <p className="text-red-400/80">- Slurred speech or increasing confusion</p>
              <p className="text-red-400/80">- Clear fluid from nose or ear</p>
            </div>
            <div className="border-t border-border/50 pt-2 mt-2">
              <p className="font-bold text-foreground">Your restrictions (ask your provider which stage):</p>
              <p className="text-muted-foreground">- Limit phone/screen time (take breaks every 30 min)</p>
              <p className="text-muted-foreground">- No driving until cleared by provider</p>
              <p className="text-muted-foreground">- No alcohol</p>
              <p className="text-muted-foreground">- No PT or exercise until cleared</p>
              <p className="text-muted-foreground">- Rest as much as possible</p>
              <p className="text-muted-foreground">- If symptoms get WORSE, not better → call your clinic</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1.5">
            <Button
              variant="default"
              size="sm"
              className="w-full h-9 text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              onClick={copyEducationPlusTracker}
              data-testid={`${testIdPrefix}-copy-all`}
              title="Copy everything — patient education, red flags, restrictions, and the fillable score template"
            >
              Copy All: Education + Tracker
            </Button>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[11px]"
                onClick={copyEducationOnly}
                data-testid={`${testIdPrefix}-copy-education`}
                title="Copy just the patient education content (symptoms list + red flags + restrictions)"
              >
                Education Only
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[11px]"
                onClick={copyTemplate}
                data-testid={`${testIdPrefix}-copy-template`}
                title="Copy just the blank fillable score template"
              >
                Tracker Only
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NSISymptomTracker;
